import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  asinUrl,
  authorUrl,
  detectStoreCode,
  getStore,
  productUrl
} from "@/lib/amazon";

const STORAGE_KEY = "tff_store";

const StoreContext = createContext(null);

export const StoreProvider = ({ children }) => {
  const [code, setCode] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved && getStore(saved).code === saved) return saved;
    } catch {
      /* private mode — fall through to detection */
    }
    return detectStoreCode();
  });

  // Whether the reader picked this store themselves (suppresses the nudge).
  const [chosen, setChosen] = useState(() => {
    try {
      return Boolean(window.localStorage.getItem(STORAGE_KEY));
    } catch {
      return false;
    }
  });

  const choose = useCallback((next) => {
    const store = getStore(next);
    setCode(store.code);
    setChosen(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, store.code);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    document.documentElement.dataset.store = code;
  }, [code]);

  const value = useMemo(
    () => ({
      code,
      store: getStore(code),
      chosen,
      choose,
      kindleUrl: productUrl(code, "kindle"),
      paperbackUrl: productUrl(code, "paperback"),
      urlFor: (asin) => asinUrl(code, asin),
      authorUrl: authorUrl(code)
    }),
    [code, chosen, choose]
  );

  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) {
    // Safe fallback so a component can never crash outside the provider.
    const code = "US";
    return {
      code,
      store: getStore(code),
      chosen: false,
      choose: () => {},
      kindleUrl: productUrl(code, "kindle"),
      paperbackUrl: productUrl(code, "paperback"),
      urlFor: (asin) => asinUrl(code, asin),
      authorUrl: authorUrl(code)
    };
  }
  return ctx;
};
